/**
 * MaterialRequestsQueue — the warehouse side of "Need Something?"
 * (MATERIAL-REQ-01, Seth 2026-09-25). On /warehouse (every open request,
 * soonest need first, filtered by urgency / On Order / Ready for Pickup) and on
 * a job's Warehouse tab (that job's requests).
 *
 * Each line moves with one tap: Pull It → Ready (and where it is: "Shelf B3"),
 * or Order It — a Pegasus PO on the job (a draft the buyer places; receiving it
 * makes the lines Ready by itself) or the ESC PO # while ESC still cuts the
 * POs — then Arrived → Ready, and Picked Up when the crew grabs it. The crew
 * sees each change on their phone as it happens. A line the reader got wrong
 * can be fixed here (and the fix teaches the next request).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock, Check, ExternalLink, MapPin, Package, PackageCheck, Pencil, Phone, Printer, ShoppingCart, Truck, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { Modal } from "@/components/ui/Modal";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { useVendors } from "@/features/items/items";
import { useItemSearch } from "@/features/invoice/officeInvoice";
import type { JobMaterial, PullStatus } from "./jobs";
import {
  URGENCY_LABEL, lineStatusText,
  useMaterialRequestQueue, useSetLineStatus, useOrderInEsc, useOrderOnPegasusPo, useFixRequestLine,
  type MaterialRequest, type QueueJob, type RequestQueue, type Urgency,
} from "./materialRequests";

type Lens = "all" | Urgency | "ordered" | "ready";
const LENSES: { k: Lens; label: string }[] = [
  { k: "all", label: "All Requests" }, { k: "today", label: "Today · ASAP" }, { k: "tomorrow_am", label: "Tomorrow AM" },
  { k: "by_date", label: "By a Date" }, { k: "order", label: "Just Order It" }, { k: "ordered", label: "On Order" }, { k: "ready", label: "Ready for Pickup" },
];
const URGENCY_TONE: Record<Urgency, string> = {
  today: "bg-rose-600 text-white", tomorrow_am: "bg-amber-100 text-amber-800", by_date: "bg-brand-50 text-brand-700", order: "bg-slate-100 text-slate-700",
};
const STATUS_TONE: Record<PullStatus, string> = {
  requested: "text-slate-500", pulling: "text-amber-700", ready: "text-emerald-700", ordered: "text-sky-700", picked_up: "text-slate-400", cancelled: "text-slate-400 line-through",
};
const day = (iso: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));
const openLine = (l: JobMaterial) => l.pull_status === "requested" || l.pull_status === "pulling";

export function MaterialRequestsQueue({ jobId, compact = false, hideWhenEmpty = false }: { jobId?: string; compact?: boolean; hideWhenEmpty?: boolean }) {
  useRealtimeRefresh(["pegasus_material_request", "pegasus_job_material"], [["material_requests_open"], ["material_requests"]]);
  const { data, isLoading } = useMaterialRequestQueue(jobId ?? null);
  const [lens, setLens] = useState<Lens>("all");
  const reqs = useMemo(() => (data?.requests ?? []).filter((r) => r.status !== "cancelled"), [data]);
  const count = (k: Lens) => reqs.filter((r) => matches(r, k)).length;
  const shown = reqs.filter((r) => matches(r, lens));

  if (isLoading) return <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />;
  if ((jobId || hideWhenEmpty) && reqs.length === 0) return null;

  return (
    <section className={cn(!compact && "rounded-xl border border-hairline bg-white p-4")}>
      {!compact && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Truck className="h-[18px] w-[18px] text-brand-500" /> {jobId ? "Material Requests" : "Warehouse Requests"}
          </h3>
          <span className="text-xs text-slate-400">What the crews asked for — pull it or order it; they see each change on their phone.</span>
        </div>
      )}
      {!jobId && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {LENSES.map((l) => {
            const n = count(l.k);
            if (l.k !== "all" && n === 0) return null;
            return (
              <button key={l.k} type="button" onClick={() => setLens(l.k)}
                className={cn("rounded-full border px-3 py-1 text-[13px] font-semibold",
                  lens === l.k ? "border-slate-900 bg-slate-900 text-white" : "border-hairline bg-white text-slate-600 hover:bg-slate-50")}>
                {l.label} <span className={cn("tabular-nums", lens === l.k ? "text-white/70" : "text-slate-400")}>{n}</span>
              </button>
            );
          })}
        </div>
      )}
      {shown.length === 0 ? (
        <p className="rounded-xl border border-dashed border-hairline py-6 text-center text-sm text-slate-400">
          {reqs.length === 0 ? "No open requests — the crews ask from their phone with “Need Something?”." : "Nothing in this view."}
        </p>
      ) : (
        <div className="space-y-3">
          {shown.map((r) => <RequestCard key={r.id} r={r} q={data!} showJob={!jobId} />)}
        </div>
      )}
    </section>
  );
}

function matches(r: MaterialRequest, k: Lens): boolean {
  if (k === "all") return true;
  if (k === "ordered") return r.lines.some((l) => l.pull_status === "ordered");
  if (k === "ready") return r.lines.some((l) => l.pull_status === "ready");
  return r.urgency === k && r.lines.some(openLine);
}

function RequestCard({ r, q, showJob }: { r: MaterialRequest; q: RequestQueue; showJob: boolean }) {
  const job: QueueJob | undefined = q.jobs.get(r.job_id);
  const memberName = useMemberName();
  const setStatus = useSetLineStatus();
  const [shelfFor, setShelfFor] = useState<string[] | null>(null);
  const [orderLines, setOrderLines] = useState<JobMaterial[] | null>(null);
  const [fixing, setFixing] = useState<JobMaterial | null>(null);
  const open = r.lines.filter(openLine);
  const orderable = r.lines.filter((l) => l.pull_status === "requested" || l.pull_status === "pulling");
  const crew = job?.crewEmpno ? memberName(job.crewEmpno) : null;
  const cell = r.requested_by_empno ? q.cells.get(r.requested_by_empno) ?? null : null;
  const who = r.requested_by_name ?? (r.requested_by ? r.requested_by.split("@")[0] : "The crew");

  return (
    <div className="rounded-2xl border border-hairline bg-white p-3.5 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn("rounded-md px-2 py-0.5 text-[12px] font-bold", URGENCY_TONE[r.urgency])}>
          <CalendarClock className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
          {URGENCY_LABEL[r.urgency]}{r.urgency === "tomorrow_am" ? " · ready by 7:00" : r.need_by && r.urgency !== "today" ? ` · ${day(r.need_by)}` : ""}
        </span>
        <span className="text-xs text-slate-500">{when(r.created_at)} · {who}</span>
        {cell && (
          <a href={`tel:${cell.replace(/[^0-9+]/g, "")}`} className="ml-auto inline-flex items-center gap-1 rounded-full border border-hairline px-2.5 py-0.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">
            <Phone className="h-3 w-3" /> Call {who.split(/\s+/)[0]}
          </a>
        )}
      </div>
      {showJob && job && (
        <Link to={`/jobs/${job.id}?tab=warehouse`} className="mt-1.5 block rounded-lg hover:bg-slate-50">
          <span className="block text-[15px] font-bold text-slate-900">{job.customer}{job.title ? <span className="font-medium text-slate-500"> — {job.title}</span> : null}</span>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <MapPin className="h-3 w-3 shrink-0" />
            {[job.installDate ? `${job.nc ? "Visit" : "Install"} ${day(job.installDate)}${job.installWindow ? ` ${job.installWindow}` : ""}` : "Not scheduled", crew, job.address].filter(Boolean).join(" · ")}
          </span>
        </Link>
      )}

      <ul className="mt-2 divide-y divide-slate-100">
        {r.lines.map((l) => {
          const s = (l.pull_status ?? "requested") as PullStatus;
          const po = l.po_id ? q.pos.get(l.po_id) ?? null : null;
          return (
            <li key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
              <span className="min-w-0 flex-1">
                <span className={cn("block text-[14px] font-semibold", s === "cancelled" ? "text-slate-400 line-through" : "text-slate-800")}>
                  {qtyText(Number(l.qty))}{l.unit && l.unit !== "ea" ? ` ${l.unit}` : " ×"} {l.description}
                  {!l.item_id && <span className="ml-1 text-[11px] font-medium text-amber-700">not in the catalog</span>}
                </span>
                {l.typed && l.typed.toLowerCase() !== l.description.toLowerCase() && <span className="block text-[11px] text-slate-400">They said: “{l.typed}”</span>}
                <span className={cn("block text-[12px] font-semibold", STATUS_TONE[s])}>
                  {lineStatusText(l, po)}
                  {po && <Link to={`/purchase-orders?po=${po.id}`} className="ml-1.5 font-medium text-brand-600 hover:underline">Open PO</Link>}
                </span>
              </span>
              <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                {s === "requested" && <Act onClick={() => setStatus.mutate({ ids: [l.id], status: "pulling" })}>Pull It</Act>}
                {(s === "requested" || s === "pulling") && <Act onClick={() => setShelfFor([l.id])} tone="good">Ready</Act>}
                {(s === "requested" || s === "pulling") && <Act onClick={() => setOrderLines([l])}>Order It</Act>}
                {s === "ordered" && <Act onClick={() => setShelfFor([l.id])} tone="good">Arrived</Act>}
                {s === "ready" && <Act onClick={() => setStatus.mutate({ ids: [l.id], status: "picked_up" })}>Picked Up</Act>}
                {(s === "picked_up" || s === "cancelled") && (
                  <button type="button" onClick={() => setStatus.mutate({ ids: [l.id], status: "requested" })} className="text-[12px] font-medium text-slate-400 hover:text-slate-600">Undo</button>
                )}
                {s !== "picked_up" && s !== "cancelled" && (
                  <>
                    <button type="button" title="Fix what it is" aria-label="Fix this line" onClick={() => setFixing(l)} className="rounded p-1 text-slate-300 hover:text-slate-600"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" title="Can't get it — cancel this line" aria-label="Cancel this line" onClick={() => setStatus.mutate({ ids: [l.id], status: "cancelled" })} className="rounded p-1 text-slate-300 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {r.note && <p className="mt-1 rounded-lg bg-slate-50 px-2.5 py-1.5 text-[13px] text-slate-600">{who.split(/\s+/)[0]}: “{r.note}”</p>}

      <div className="mt-2.5 flex flex-wrap gap-2">
        {open.length > 1 && (
          <button type="button" onClick={() => setShelfFor(open.map((l) => l.id))} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
            <Check className="h-3.5 w-3.5" /> Mark All Ready
          </button>
        )}
        {orderable.length > 1 && (
          <button type="button" onClick={() => setOrderLines(orderable)} className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
            <ShoppingCart className="h-3.5 w-3.5" /> Order the Rest on One PO
          </button>
        )}
        <a href={`/jobs/${r.job_id}/pick-sheet`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          <Printer className="h-3.5 w-3.5" /> Print Pick Sheet
        </a>
        {!showJob ? null : (
          <Link to={`/jobs/${r.job_id}?tab=warehouse`} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-brand-700 hover:text-brand-900">
            <ExternalLink className="h-3.5 w-3.5" /> The job
          </Link>
        )}
      </div>
      {setStatus.isError && <p className="mt-1.5 text-xs text-rose-600">Didn&apos;t save — try again.</p>}

      {shelfFor && <ShelfPrompt count={shelfFor.length} lines={r.lines} onClose={() => setShelfFor(null)}
        onSave={(shelf) => setStatus.mutate({ ids: shelfFor, status: "ready", shelf }, { onSuccess: () => setShelfFor(null) })} saving={setStatus.isPending} />}
      {orderLines && job && <OrderModal job={job} lines={orderLines} who={who} onClose={() => setOrderLines(null)} />}
      {fixing && <FixLineModal line={fixing} onClose={() => setFixing(null)} />}
    </div>
  );
}

function Act({ children, onClick, tone }: { children: React.ReactNode; onClick: () => void; tone?: "good" }) {
  return (
    <button type="button" onClick={onClick}
      className={cn("rounded-md px-2.5 py-1 text-xs font-semibold",
        tone === "good" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-hairline bg-white text-slate-700 hover:bg-slate-50")}>
      {children}
    </button>
  );
}

/** Where it's staged — the crew reads it on their phone. */
function ShelfPrompt({ count, lines, onSave, onClose, saving }: { count: number; lines: JobMaterial[]; onSave: (shelf: string) => void; onClose: () => void; saving: boolean }) {
  const recent = [...new Set(lines.map((l) => l.shelf).filter(Boolean) as string[])].slice(0, 4);
  const [shelf, setShelf] = useState(recent[0] ?? "");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(shelf); }} className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-2.5">
      <PackageCheck className="h-4 w-4 text-emerald-600" />
      <span className="text-[13px] font-semibold text-emerald-800">{count > 1 ? `${count} ready` : "Ready"} — where is it?</span>
      <input autoFocus value={shelf} onChange={(e) => setShelf(e.target.value)} placeholder="e.g. Shelf B3 · Mike's cart"
        className="min-w-[160px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm" />
      {recent.filter((x) => x !== shelf).map((x) => (
        <button key={x} type="button" onClick={() => setShelf(x)} className="rounded-full border border-hairline bg-white px-2 py-0.5 text-[12px] text-slate-600">{x}</button>
      ))}
      <button type="submit" disabled={saving} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:bg-emerald-300">{saving ? "Saving…" : "Save"}</button>
      <button type="button" onClick={onClose} className="text-xs text-slate-500">Cancel</button>
    </form>
  );
}

/** Order It: a Pegasus PO on the job, or the ESC PO # (ESC still cuts the POs today). */
function OrderModal({ job, lines, who, onClose }: { job: QueueJob; lines: JobMaterial[]; who: string; onClose: () => void }) {
  const { data: vendors } = useVendors();
  const onPo = useOrderOnPegasusPo();
  const inEsc = useOrderInEsc();
  const [how, setHow] = useState<"pegasus" | "esc">("pegasus");
  const [vendor, setVendor] = useState("");
  const [vq, setVq] = useState("");
  const [due, setDue] = useState(job.installDate ?? "");
  const [escPo, setEscPo] = useState("");
  const [pick, setPick] = useState<Set<string>>(() => new Set(lines.map((l) => l.id)));
  const chosen = lines.filter((l) => pick.has(l.id));
  const vlist = (vendors?.list ?? []).filter((v) => !vq.trim() || `${v.name ?? ""} ${v.code}`.toLowerCase().includes(vq.trim().toLowerCase())).slice(0, 30);
  const busy = onPo.isPending || inEsc.isPending;
  const ok = chosen.length > 0 && (how === "pegasus" ? !!vendor : !!escPo.trim()) && !busy;
  const save = () => {
    if (!ok) return;
    if (how === "pegasus") {
      onPo.mutate({ job: { id: job.id, custno: job.custno, locno: job.locno }, vendorCode: vendor, due: due || null, lines: chosen,
        memo: `Material request — ${who} · ${job.customer}` }, { onSuccess: onClose });
    } else {
      inEsc.mutate({ ids: chosen.map((l) => l.id), escPo, due: due || null }, { onSuccess: onClose });
    }
  };
  return (
    <Modal open onClose={onClose} title="Order It" subtitle={`${job.customer}${job.title ? ` — ${job.title}` : ""}`} size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-hairline px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="button" disabled={!ok} onClick={save} className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white disabled:bg-brand-300">
            {busy ? "Saving…" : how === "pegasus" ? "Make the PO" : "Mark On Order"}
          </button>
        </div>
      }>
      <div className="space-y-3 text-sm">
        <ul className="space-y-1">
          {lines.map((l) => (
            <li key={l.id}>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={pick.has(l.id)} onChange={() => setPick((p) => { const n = new Set(p); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n; })} />
                <span className="text-slate-700">{qtyText(Number(l.qty))}{l.unit && l.unit !== "ea" ? ` ${l.unit}` : " ×"} {l.description}</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="inline-flex rounded-lg border border-hairline bg-slate-50 p-0.5">
          {([["pegasus", "Make a Pegasus PO"], ["esc", "It's in ESC — Type the PO #"]] as const).map(([k, label]) => (
            <button key={k} type="button" onClick={() => setHow(k)}
              className={cn("rounded-md px-3 py-1 text-xs font-semibold", how === k ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-500")}>{label}</button>
          ))}
        </div>
        {how === "pegasus" ? (
          <div className="space-y-1.5">
            <input value={vq} onChange={(e) => setVq(e.target.value)} placeholder="Find the vendor — Johnstone, Ferguson…" className="w-full rounded-md border border-hairline px-2.5 py-1.5" />
            <div className="max-h-40 overflow-y-auto rounded-md border border-hairline">
              {vlist.map((v) => (
                <button key={v.code} type="button" onClick={() => setVendor(v.code)}
                  className={cn("block w-full px-2.5 py-1.5 text-left text-[13px]", vendor === v.code ? "bg-brand-50 font-semibold text-brand-800" : "hover:bg-slate-50")}>
                  {v.name ?? v.code} <span className="text-[11px] text-slate-400">{v.code}</span>
                </button>
              ))}
              {vlist.length === 0 && <p className="px-2.5 py-2 text-xs text-slate-400">No vendor by that name.</p>}
            </div>
            <p className="text-xs text-slate-500">Makes a draft PO on this job with these lines — place it with the vendor from Purchase Orders. When it&apos;s received, the lines turn Ready on the crew&apos;s phone by themselves.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            <input value={escPo} onChange={(e) => setEscPo(e.target.value)} placeholder="ESC PO number" className="w-full rounded-md border border-hairline px-2.5 py-1.5" />
            <p className="text-xs text-slate-500">For a PO you cut in ESC — the crew sees “On order · ESC PO …”. Tap Arrived when it comes in.</p>
          </div>
        )}
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">Expected</span>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-0.5 block rounded-md border border-hairline px-2.5 py-1.5" />
        </label>
        {(onPo.isError || inEsc.isError) && <p className="text-xs text-rose-600">{((onPo.error ?? inEsc.error) as Error)?.message ?? "Didn't save."}</p>}
      </div>
    </Modal>
  );
}

/** Fix a line the reader got wrong — the right catalog item teaches the next request. */
function FixLineModal({ line, onClose }: { line: JobMaterial; onClose: () => void }) {
  const fix = useFixRequestLine();
  const [desc, setDesc] = useState(line.description);
  const [qty, setQty] = useState(String(line.qty));
  const [q, setQ] = useState("");
  const { data: hits } = useItemSearch(q);
  const [item, setItem] = useState<{ id: string; description: string | null } | null>(null);
  return (
    <Modal open onClose={onClose} title="Fix This Line" subtitle={line.typed ? `They said: “${line.typed}”` : undefined} size="md"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-hairline px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="button" disabled={fix.isPending || !desc.trim()}
            onClick={() => fix.mutate({ line, description: item?.description ?? desc, qty: Number(qty) || Number(line.qty) || 1, item: item ? { id: item.id } : null }, { onSuccess: onClose })}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white disabled:bg-brand-300">{fix.isPending ? "Saving…" : "Save"}</button>
        </div>
      }>
      <div className="space-y-3 text-sm">
        <div className="flex gap-2">
          <input value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} aria-label="Quantity" className="w-20 rounded-md border border-hairline px-2.5 py-1.5 text-right" />
          <input value={item?.description ?? desc} onChange={(e) => { setItem(null); setDesc(e.target.value); }} aria-label="What it is" className="min-w-0 flex-1 rounded-md border border-hairline px-2.5 py-1.5" />
        </div>
        <div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or find it in the catalog — part #, name…" className="w-full rounded-md border border-hairline px-2.5 py-1.5" />
          {q.trim().length >= 2 && (
            <ul className="mt-1 max-h-44 overflow-y-auto rounded-md border border-hairline">
              {(hits ?? []).slice(0, 10).map((h) => (
                <li key={h.id}>
                  <button type="button" onClick={() => { setItem({ id: h.id, description: h.description }); setQ(""); }}
                    className="block w-full px-2.5 py-1.5 text-left text-[13px] hover:bg-slate-50">
                    {h.description ?? h.part_number} <span className="font-mono text-[11px] text-slate-400">{h.part_number}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {item && <p className="mt-1 text-xs text-emerald-700"><Package className="mr-1 inline h-3 w-3" />Matched to the catalog — next time “{line.typed ?? line.description}” reads as this.</p>}
        </div>
        {fix.isError && <p className="text-xs text-rose-600">Didn&apos;t save — try again.</p>}
      </div>
    </Modal>
  );
}

